import React from 'react';
import { Home, ChevronRight, Volume2, Users, ArrowRight, ArrowLeft } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface JohnFrederickSectionProps {
  onSelectPart?: (partId: string) => void;
}

export const JohnFrederickSection: React.FC<JohnFrederickSectionProps> = ({ onSelectPart }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 font-sans text-[#2c221e]">
      
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          
          {/* Breadcrumb: The Dentons > Part 4 of 9: John Frederick & Eliza DENTON */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPart && onSelectPart('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Dentons</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Part 4 of 9: John Frederick &amp; Eliza DENTON
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
            title="Listen to John Frederick & Eliza Denton"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          JOHN FREDERICK AND ELIZA DENTON – 9 children in 18 years then widowhood
        </h1>

        <p className="font-serif text-lg md:text-xl font-bold text-[#7a5b3a] italic leading-snug">
          The story of a young marriage, a big family, bankruptcy, an earthquake, a ballot and an early death
        </p>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Users className="w-3.5 h-3.5" />
          <span>Part 4 of the 9-Part Denton Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-10 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Section 1: Marriage and Illness */}
        <section className="space-y-6">
          <p>
            When Eliza Jane Giles, just 17, stood beside handsome John Frederick Denton,21, at St Thomas Church at East Eyreton on 5 October 1882 little would they have thought that 20 years later Eliza would be left widowed with 9 children and living in a sod cottage on an 89-ha farm.
          </p>

          <p>
            Only three years after their marriage, John, who was known as Jack, was diagnosed with Addisons Disease, a disorder in which the adrenal glands don’t produce enough hormones and which weaken the body when under stress from infections. Symptoms include fatigue, nausea, dizziness on standing, darkening of the skin and low blood pressure – these surely would have affected his farm work which at times would have been strenuous – harvesting, shearing, ground cultivation, fencing, etc. These days Addisons is treated with hormone replacement therapy and steroids.
          </p>

          {/* Images 66 & 67: Eliza with Elsie & Jack with Margaret, Lance, Harry */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div className="space-y-2">
              <ArchivalImageCard
                src="/images/66 Eliza with Elsie 1893.jpg"
                alt="Eliza with Elsie 1893"
                caption="Eliza with Elsie in 1893, five years before Jack died."
                filenameHint="66 Eliza with Elsie 1893.jpg"
              />
              <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
                Eliza with Elsie in 1893, five years before Jack died.
              </p>
            </div>

            <div className="space-y-2">
              <ArchivalImageCard
                src="/images/67 Jack Denton with Margaret, Lance and Harry.jpg"
                alt="Jack Denton with Margaret, Lance and Harry"
                caption="Jack with Margaret, Lance and young Harry"
                filenameHint="67 Jack Denton with Margaret, Lance and Harry.jpg"
              />
              <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
                Jack with Margaret, Lance and young Harry
              </p>
            </div>
          </div>

          <p>
            When John died at Rangiora on 3 August 1902 Eliza was left with her little household of heartbroken children.
          </p>

          {/* Children List in 2 columns */}
          <div className="bg-[#f3ede0]/80 p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 font-serif text-[#2c221e]">
              <ul className="space-y-2">
                <li><strong>Margaret Esther</strong>, aged 19</li>
                <li><strong>Lancelot Frederick</strong>, aged 17</li>
                <li><strong>John Thomas (Tommy)</strong>, aged 15</li>
                <li><strong>Harry</strong>, aged 13</li>
                <li><strong>Elsie May</strong>, aged 11</li>
              </ul>
              <ul className="space-y-2">
                <li><strong>Florence Edith</strong>, aged 9</li>
                <li><strong>Ivy</strong>, aged 7</li>
                <li><strong>Queen Eileen</strong>, aged 4</li>
                <li><strong>Jane</strong>, aged 3</li>
              </ul>
            </div>
          </div>

          <p>
            Eliza carried on at the Domett farm that she had won in the Cheviot Settlement Ballot 1894.
          </p>

          <p>
            The new Liberal Government of 1891 had promised to break up huge properties to make them available for small farmers. The first was the 34,000 ha sheep run called Cheviot Hills, which the government, under Lands Minister John McKenzie, bought for 260,220 pounds (equivalent to about $34.7 million in 2021). The previous owner William “Ready Money” Robinson had died in 1889 and his estate was left in trust for 5 daughters. The political ideology behind this was largely influenced by the experience of Irish and Scottish farmers before they came to NZ where there was a promise of a more utopian and fairer system.
          </p>

          <p>
            The land was balloted for cash payment, grazing run leases and leases in perpetuity. Land was set aside for Cheviot village sections. Three ballots were held between November 1893 and May 1894 to allocate the land.
          </p>
        </section>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 2: Cheviot Estate Ballot & Financial Struggles */}
        <section className="space-y-6">
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border border-[#d8ceb9] space-y-3">
            <div className="text-xs font-serif text-[#7a5b3a] font-bold uppercase tracking-wider">
              From The Christchurch Star, 15 May 1894
            </div>
            <h3 className="font-serif text-xl sm:text-2xl font-extrabold text-[#2c221e]">
              THE CHEVIOT ESTATE
            </h3>
            <h4 className="font-serif text-lg font-bold text-[#5c4328]">
              Ballot For Sections
            </h4>
            <blockquote className="italic font-serif text-base sm:text-lg text-[#3d2a1d] leading-relaxed border-l-2 border-[#7a5b3a] pl-4 pt-1">
              At the Land Office today a ballot was taken to decide priority of selecting 17,591 acres opened on the Cheviot Estate for lease on applications. The land was very eagerly sought after, 117 applications seeking 47 lots of the 50 opened. Among the applicants were 24 married women and 18 single women.
            </blockquote>
          </div>

          <p className="font-serif font-bold text-lg text-[#4a3525]">
            Why did Eliza enter the ballot? Jack had been declared bankrupt in September 1894.
          </p>

          <p>
            Jack and Eliza leased a farm at Cabbage Tree Flat until 1894, after Jack’s father gave him a loan of 100 pounds (about $12,500 at 2021 values). After 12 years Jack got into financial difficulties and was declared bankrupt. At the creditors meeting 5 September 1894 Jack said he had bought sheep from his father in April 1893 and was to pay for them as soon as he could. In late May 1894 his father removed the sheep from the property, branded them with his brand, and put them on Bix Denton’s land (Jack’s brother). The sheep Jack’s father took were to fully satisfy the earlier loan - Jack wrote that it was the first time his father asked for payment of neat debt. He was evicted by the landlord, taking his goods and chattels onto a wagon.
          </p>

          <p>
            But there was a lot of happiness as well. This is taken from “A Family of Canterbury Settlers: The Denton Family 1850-2000” by Adair Dunsford and Russ Denton.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-4 shadow-xs">
            <p className="italic">
              “The newlyweds moved to Motunau and in extracts from T.E.L. Roberts’ Collection of Memoirs of Motunau or The Hills of Home he records that about 1883 John Denton and his young wife, both of Eyreton, took a lease of a block of land just above Craig Knock homestead. Denton built a cottage on his block and having a team of horses set to work to grow chaff for them following the role of contractor in general.
            </p>
            <p className="italic">
              With the advent of Dentons, there also came to Greig’s farm, a man named Bennett…These two settlers made quire a difference to our social outlook as we were now quite a little community in which the very best of good fellowship ruled. The young couple, Dentons, had sisters and these at times found place in our attempts at sociality, the importance of which could hardly be overrated in a world of bachelors, the potential settlers of the future. There were pretty little incidents of meeting and parting and remembering even unto the ends of time, yes and there are hearts still beating to the tune of those far off measures under the waistcoats of some of my immediate seniors.”
            </p>
          </blockquote>

          <p>
            When Eliza won the ballot for land at Domett they built a sod cottage, the remains of which stand today.
          </p>

          {/* Image 68: Eliza 1895 at Domett */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/68 Eliza 1895 at Domett.jpg"
              alt="Eliza 1895 at Domett"
              caption="Eliza with Ivy, Elsie, Harry, Tommy and Margaret, about 1895"
              filenameHint="68 Eliza 1895 at Domett.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Eliza with Ivy, Elsie, Harry, Tommy and Margaret, about 1895
            </p>
          </div>

          <p>
            Jack worked on threshing mills in the district, on the Domett farm, and on Milburn Farm next door, which belonged to his brother Bix (Albert Edward) who had married his wife Eliza’s sister Rebecca.
          </p>
        </section>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 3: The 1901 Cheviot Earthquake and Later Life */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              The 1901 Cheviot Earthquake and Later Years
            </h2>
          </div>

          <p>
            The 1901 Cheviot earthquake had an estimated 6.8 magnitude. A local baby was killed when a sod hut collapsed, the top of the spire of Christchurch Cathedral toppled off and there was liquefaction in Kaiapoi on the north bank of the Kaiapoi River. Photographs showed women cooking outdoors over open fires, damaged buildings, road fissures and rock falls. There are about 35 faults in the Hurunui District. Jack and Eliza’s great granddaughter, Shirley Sheil, experienced the November 2014 two-minute long 7.8 magnitude Kaikoura earthquake, at Cheviot.
          </p>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border border-[#d8ceb9] space-y-3">
            <div className="text-xs font-serif text-[#7a5b3a] font-bold uppercase tracking-wider">
              The Christchurch Star reported on The Earthquakes at Cheviot (1901)
            </div>
            <blockquote className="italic font-serif text-base sm:text-lg text-[#3d2a1d] leading-relaxed border-l-2 border-[#7a5b3a] pl-4 pt-1">
              “The series of earthquake shocks which started on Saturday morning, and which have continued at intermittent intervals up the time of writing, have been felt in varying degrees of intensity in difference districts. It is at Cheviot, however, that the chief interest centres at the present moment, for there the disturbance has been of unusually severe description. With Cheviot, and the earthquake visitation, the principal topic of conversation, special interest attaches to the current issue of the “Canterbury Times” in which will be found an excellent pictorial record of the effects of the shocks in the unfortunate district.”
            </blockquote>
          </div>

          <p>
            After Jack’s death Eliza carried on with the farm, helped by her sons and relatives, until she married Irish-born widower James George Murchison at her father’s house in Kaiapoi on 26 April 1906. In 1910 they had James George Murchison, known as Joe. Eliza left her house at 4 Buffon Street Christchurch to that son.
          </p>

          <p>
            Eliza died 29 October 1938 at Christchurch Hospital, having been taken there from her home. Her death certificate states the cause of death of chronic endocarditis, myocarditis with hypertrophy and anasarca, and general arteria sclerosis.
          </p>

          <p>
            Eliza is buried at Sydenham Cemetery, Block 37C Plot 69. The cemetery is on Simeon Street between Barrington Street and Selwyn Street. There is no headstone in evidence.
          </p>

          {/* Image 69: Queenie's wedding to Harry Kidd 1916 */}
          <div className="space-y-3 my-6">
            <ArchivalImageCard
              src="/images/69 Queenie_s wedding to Harry Kidd 1916.jpg"
              alt="Queenie's wedding to Harry Kidd 1916"
              caption="Eliza at her daughter Queen Eileen’s wedding to Harry Young Kidd, 26 June 1916"
              filenameHint="69 Queenie_s wedding to Harry Kidd 1916.jpg"
            />
            <div className="font-serif text-sm md:text-base text-[#5c4328] space-y-1 text-center sm:text-left">
              <p className="font-bold">
                Eliza at her daughter Queen Eileen’s wedding to Harry Young Kidd, 26 June 1916
              </p>
              <p>
                <strong>Back L-R:</strong> Florence, Lancelot, Elsie, Margaret, Harry &amp; Ivy
              </p>
              <p>
                <strong>Seated:</strong> Joe Murchison, Eliza’s son with James Murchison
              </p>
            </div>
          </div>
        </section>

      </article>

      {/* Chapter Bottom Navigation */}
      <footer className="flex flex-col sm:flex-row justify-between items-center gap-4 p-5 sm:p-6 bg-[#f3ede0] rounded-3xl border-2 border-[#d8ceb9] shadow-sm">
        <button
          onClick={() => onSelectPart && onSelectPart('part3')}
          className="flex items-center gap-2 text-xs sm:text-sm font-bold text-[#7a5b3a] hover:text-[#2c221e] hover:underline font-serif transition cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Previous: Part 3 — Frederick Robert &amp; Esther Denton</span>
        </button>
        <button
          onClick={() => onSelectPart && onSelectPart('part5')}
          className="flex items-center gap-2 text-xs sm:text-sm font-bold text-[#7a5b3a] hover:text-[#2c221e] hover:underline font-serif transition cursor-pointer"
        >
          <span>Next: Part 5 — Annie Denton nee Gibb</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </footer>

    </div>
  );
};
